import { LogLevel } from '@azure/msal-browser'

const config = window.__APP_CONFIG__ || __APP_CONFIG__

/**
 * The Entra identifiers, resolved at RUNTIME rather than read from the build.
 *
 * A customer's tenant is unknown when this bundle is built: it exists only
 * once their admin grants consent, which happens after provisioning. So the
 * baked values are empty strings for every new customer, MSAL is constructed
 * without a client_id, and the first sign-in fails with AADSTS900144.
 *
 * Baked values win when present — an established customer keeps working with
 * no extra request, and this cannot regress them. Otherwise we ask the
 * worker, which holds the identifiers pushed at consent. Discovery already
 * runs before login for the API URL; this rides the same endpoint.
 *
 * apiClient's discovery hits that same URL at module load. Sharing one request
 * would mean importing apiClient here, and apiClient imports main.jsx, which
 * imports this — a cycle. The duplicate is one GET against a 60s-cacheable
 * response, and only for a customer whose identifiers were never baked.
 */
export async function resolveAuthIdentity() {
  const baked = {
    clientId: (config.azureClientId || '').trim(),
    tenantId: (config.azureTenantId || '').trim(),
  }
  if (baked.clientId && baked.tenantId) return baked

  try {
    const res = await fetch(`${config.apiUrl}/api/vanity-config`)
    if (res.ok) {
      const data = await res.json()
      const clientId = typeof data.azureClientId === 'string' ? data.azureClientId.trim() : ''
      const tenantId = typeof data.azureTenantId === 'string' ? data.azureTenantId.trim() : ''
      if (clientId && tenantId) return { clientId, tenantId }
    }
  } catch {
    // Fall through — the caller reports the unconfigured state rather than
    // constructing MSAL with an empty client_id and failing at Microsoft.
  }
  return baked
}

export function buildMsalConfig({ clientId, tenantId }) {
  return {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri: window.location.origin,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true, // Critical: prevents redirect loops
    },
    system: {
      allowNativeBroker: false,
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return
          switch (level) {
            case LogLevel.Error:
              console.error(message)
              return
            case LogLevel.Warning:
              console.warn(message)
              return
            case LogLevel.Info:
              console.info(message)
              return
            case LogLevel.Verbose:
              console.debug(message)
              return
          }
        },
        logLevel: LogLevel.Warning,
      },
    },
  }
}

export const loginRequest = {
  scopes: ['User.Read', 'openid', 'profile', 'email'],
}
